/**
 * Analytics & Tracking Hooks
 * React Query hooks for fetching analytics and tracking data
 */

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import type { ApiSuccessResponse } from '../types/api';
import { API_URL } from '../services/api';
import type {
  AnalyticsDashboard,
  SearchAnalytics,
  CompanyAnalytics,
  AnalyticsFilters
} from '../types/tracking';

// ====================
// Analytics Dashboard Hook
// ====================

/**
 * Hook สำหรับดึงข้อมูล Analytics Dashboard ทั้งหมด
 * รวม page views, searches, contacts, และ popular companies
 */
export function useAnalyticsDashboard() {
  return useQuery({
    queryKey: ['analytics', 'dashboard'],
    queryFn: async () => {
      const response = await axios.get<ApiSuccessResponse<AnalyticsDashboard>>(
        `${API_URL}/api/tracking/analytics/dashboard`
      );
      return response.data.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: 1000 * 60 * 5, // Auto-refetch every 5 minutes
  });
}

// ====================
// Search Analytics Hook
// ====================

/**
 * Hook สำหรับดึงข้อมูล Search Analytics จาก dedicated endpoint
 * เชื่อมต่อกับ GET /api/admin/analytics/search
 */
export function useSearchAnalytics(filters?: AnalyticsFilters) {
  return useQuery({
    queryKey: ['analytics', 'search', filters],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters?.days) {
        params.append('days', filters.days.toString());
      } else {
        params.append('days', '30');
      }

      if (filters?.user_type) {
        params.append('user_type', filters.user_type);
      } else {
        params.append('user_type', 'all');
      }

      const response = await axios.get<ApiSuccessResponse<SearchAnalytics>>(
        `${API_URL}/api/admin/analytics/search?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      return response.data.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

// ====================
// Company Analytics Hook
// ====================

/**
 * Hook สำหรับดึงข้อมูล Analytics ของบริษัทเฉพาะ
 */
export function useCompanyAnalytics(companyId: number, days: number = 30) {
  return useQuery({
    queryKey: ['analytics', 'company', companyId, days],
    queryFn: async () => {
      const response = await axios.get<ApiSuccessResponse<CompanyAnalytics>>(
        `${API_URL}/api/tracking/analytics/company/${companyId}`,
        {
          params: { days }
        }
      );
      return response.data.data;
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ====================
// Analytics Summary Hook
// ====================

/**
 * Hook สำหรับดึงสรุป Analytics ช่วงเวลาที่กำหนด
 */
export function useAnalyticsSummary(days: number = 30) {
  return useQuery({
    queryKey: ['analytics', 'summary', days],
    queryFn: async () => {
      const response = await axios.get<ApiSuccessResponse>(
        `${API_URL}/api/tracking/analytics/summary`,
        {
          params: { days }
        }
      );
      return response.data.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
